import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Heart, Users, FileText, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { servicesList, valuesToRespect, statsData } from '../utils/mockData';

const Home = () => {
  const iconMap = {
    Home: Users,
    Heart: Heart,
    Users: Users,
    FileText: FileText,
    Shield: Shield,
    TrendingUp: ArrowRight,
    BookOpen: FileText,
    Globe: Users,
    Award: CheckCircle
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="bg-white/80 backdrop-blur-sm text-[#4A90E2] px-4 py-2 rounded-full text-sm font-semibold">
                  RMN-Led Mental Health Care
                </span>
              </div>
              <h1 className="heading-hero">
                The Spheres That <span className="text-gradient">Support Life</span>
              </h1>
              <p className="body-large max-w-xl">
                Compassionate, evidence-based mental health support and staffing solutions for adults across the United Kingdom.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] hover:shadow-lg transition-all">
                    Request a Call Back
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-[#4A90E2] text-[#4A90E2] hover:bg-[#4A90E2] hover:text-white"
                  >
                    Our Services
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
                {statsData.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-[#4A90E2]">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtZW50YWwlMjBoZWFsdGglMjBjYXJlfGVufDB8fHx8MTc3MDQ1MTc2Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Mental Health Support"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="heading-2">Who We Are</h2>
            <p className="body-large">
              Three Spheres Healthcare is a UK-based, mental-health-led care organisation dedicated to supporting adults with enduring mental health conditions and associated disabilities to live safe, meaningful, and fulfilling lives within the community.
            </p>
            <p className="body-medium">
              Founded and led by a Registered Mental Health Nurse (RMN), we bring together frontline clinical expertise, academic insight, and strong governance principles to deliver high-quality, person-centred supported living services. Our approach is grounded in compassion, dignity, safety, and evidence-based practice.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Services</h2>
            <p className="body-large max-w-2xl mx-auto">
              Comprehensive mental health support and staffing solutions tailored to your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicesList.map((service) => {
              const Icon = iconMap[service.icon] || Users;
              return (
                <Card key={service.id} className="p-6 hover:shadow-xl transition-all duration-300 border-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">{service.title}</h3>
                  <p className="body-medium text-gray-600 mb-4">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-[#4A90E2] font-semibold text-sm flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    Learn More <ArrowRight size={16} />
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Values</h2>
            <p className="body-large max-w-2xl mx-auto">
              Health and wellbeing are sustained through balance, structure, and connection.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {valuesToRespect.map((value) => {
              const Icon = iconMap[value.icon] || Shield;
              return (
                <div 
                  key={value.id}
                  className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <Icon className="text-[#4A90E2] mb-4" size={32} />
                  <h3 className="heading-3 mb-3">{value.title}</h3>
                  <p className="body-medium text-gray-700">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Ready to Work Together?</h2>
            <p className="body-large text-white/90">
              Whether you're a commissioner looking for quality care services or a healthcare professional seeking meaningful work, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-[#4A90E2] hover:bg-gray-100"
                >
                  Make a Referral
                </Button>
              </Link>
              <Link to="/recruitment">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#4A90E2]"
                >
                  Join Our Team
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-2 pt-4">
              <Phone size={20} />
              <a href="tel:03300433693" className="text-lg font-semibold hover:underline">
                0330 043 3693
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
